import React from 'react';

import Button from '@/components/ui/buttons/Button';
import ErrorList from '@/components/ui/forms/ErrorList';
import Modal from '@/components/ui/modals/Modal';
import { type ModalInnerProps } from '@/hooks/common/useModal';
import useUnlinkParentOrganizationMembership from '@/hooks/mypage/useUnlinkParentOrganizationMembership';
import useToast from '@/hooks/store/useToast';

import CSSModule from './UnlinkConfirmModal.module.scss';

interface Props extends ModalInnerProps {
  parentId: number;
  code: string;
  targetName: string;
  description: React.ReactNode;
  showNotifyEmailNotice: boolean;
}

const UnlinkConfirmModal: React.FC<Props> = ({
  isOpen,
  toggle,
  parentId,
  code,
  targetName,
  description,
  showNotifyEmailNotice,
}) => {
  const { showToast } = useToast();
  const { mutate, isPending, error, refetchMe } =
    useUnlinkParentOrganizationMembership({ parentId, code });

  // 5xx / NW 障害はリトライで回復しうるためダイアログ内に文言を残す。
  // mutate 再実行時に error はリセットされるので別途クリアは不要。
  const retryableErrorMessage =
    error && error.status >= 500
      ? '予期しないエラーが発生しました。時間をおいて再度お試しください'
      : '';

  const handleConfirm = () => {
    mutate(undefined, {
      onSuccess: () => {
        void refetchMe();
        showToast('紐付けを解除しました');
        toggle();
      },
      onError: (err) => {
        // 5xx / NW 障害はモーダルを閉じず、retryableErrorMessage を表示して再試行を促す
        if (err.status >= 500) {
          return;
        }
        void refetchMe();
        // no_parent_error は解除できない理由のユーザー向け文言が API から返るため、それを表示する
        showToast(
          err.error_code === 'no_parent_error' && err.json.message
            ? err.json.message
            : '紐付け解除でエラーが発生しました',
        );
        toggle();
      },
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} title="本当に紐付けを解除しますか？">
      <div className={CSSModule.UnlinkConfirmModal}>
        <div className={CSSModule.UnlinkConfirmModal__Content}>
          <p className={CSSModule.UnlinkConfirmModal__Description}>
            {description}
          </p>
          <p className={CSSModule.UnlinkConfirmModal__Confirm}>
            本当に
            <span className={CSSModule.UnlinkConfirmModal__TargetName}>
              {targetName}
            </span>
            さんの紐付けを解除しますか？
          </p>
          {showNotifyEmailNotice && (
            <p className={CSSModule.UnlinkConfirmModal__Confirm}>
              ※紐付けを解除すると、解除された相手に通知メールが自動送信されます。
            </p>
          )}
          {retryableErrorMessage && (
            <ErrorList errors={[retryableErrorMessage]} />
          )}
        </div>
        <div className={CSSModule.UnlinkConfirmModal__Buttons}>
          <Button
            color="outline"
            size="s"
            className={CSSModule.UnlinkConfirmModal__Button}
            onClick={toggle}
            disabled={isPending}
          >
            キャンセル
          </Button>
          <Button
            color="danger"
            size="s"
            className={CSSModule.UnlinkConfirmModal__Button}
            onClick={handleConfirm}
            disabled={isPending}
          >
            解除
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UnlinkConfirmModal;
